import React, { useState, useEffect, useCallback, useRef } from 'react';
import { isDisplayableProjectImage } from '../utils/projectImages';
import './AutoImageCarousel.css';

const AutoImageCarousel = ({ images, interval = 3500, embedded = false }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [validImages, setValidImages] = useState(images);
  const timerRef = useRef(null);

  useEffect(() => {
    let isMounted = true;

    const checkImages = async () => {
      const photoImages = images.filter((image) => isDisplayableProjectImage(image.src));
      const loaded = await Promise.all(
        photoImages.map(
          (image) =>
            new Promise((resolve) => {
              const img = new Image();
              img.onload = () => {
                if (img.naturalWidth >= 800) {
                  resolve(image);
                } else {
                  resolve(null);
                }
              };
              img.onerror = () => resolve(null);
              img.src = image.src;
            })
        )
      );

      if (isMounted) {
        const filtered = loaded.filter(Boolean);
        setValidImages(filtered.length ? filtered : photoImages);
        setCurrentIndex(0);
      }
    };

    checkImages();

    return () => {
      isMounted = false;
    };
  }, [images]);

  const goToNext = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % validImages.length);
  }, [validImages.length]);

  const goToPrev = useCallback(() => {
    setCurrentIndex((prev) => (prev - 1 + validImages.length) % validImages.length);
  }, [validImages.length]);

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const startTimer = useCallback(() => {
    clearTimer();
    if (validImages.length <= 1) return;
    timerRef.current = setInterval(goToNext, interval);
  }, [clearTimer, goToNext, interval, validImages.length]);

  useEffect(() => {
    startTimer();
    return clearTimer;
  }, [startTimer, clearTimer]);

  const handleControlClick = (event, action) => {
    event.stopPropagation();
    action();
    startTimer();
  };

  if (!validImages.length) return null;

  return (
    <div className={`auto-image-carousel${embedded ? ' embedded' : ''}`}>
      <div className="carousel-track">
        {validImages.map((image, index) => (
          <div
            key={image.src}
            className={`carousel-slide ${index === currentIndex ? 'active' : ''}`}
          >
            <img
              src={image.src}
              alt={image.alt}
              loading={index <= 1 ? 'eager' : 'lazy'}
              decoding="async"
            />
          </div>
        ))}

        {validImages.length > 1 && (
          <>
            <button
              type="button"
              className="carousel-arrow carousel-arrow-prev"
              aria-label="Previous image"
              onClick={(event) => handleControlClick(event, goToPrev)}
            >
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <button
              type="button"
              className="carousel-arrow carousel-arrow-next"
              aria-label="Next image"
              onClick={(event) => handleControlClick(event, goToNext)}
            >
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </>
        )}
      </div>

      {validImages.length > 1 && (
        <div className="carousel-dots">
          {validImages.map((image, index) => (
            <button
              key={image.src}
              type="button"
              className={`carousel-dot ${index === currentIndex ? 'active' : ''}`}
              aria-label={`Show image ${index + 1}`}
              onClick={(event) => handleControlClick(event, () => setCurrentIndex(index))}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default AutoImageCarousel;
